import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import axiosInstance, {
  checkTokenValidity,
  CheckUserExpiry,
  getUserDetails,
  getStaffId,
  isFisherWideUser,
  hasScientistPondUIAccess,
  isIndependentScientistUser,
  isCompanyManagerUser,
  isSuperUserSession,
} from "../../components/content/axiosConfig";
import Swal from "sweetalert2";
import Preloader from "../../components/custom/preLoader";
import showErrorToast, { showSuccessToast } from "../../components/toast/toast";
import { formatDisplayDate } from "../../utils/formatDisplayDate";
import { getScopedAccountId } from "../../utils/accountContext";

function typeTone(type) {
  const t = String(type || "").toLowerCase();
  if (t.includes("natural")) return "natural";
  if (t.includes("stock") || t.includes("farm") || t.includes("artificial")) return "stocked";
  if (t.includes("river") || t.includes("stream")) return "river";
  return "other";
}

function pondInitial(name) {
  const s = String(name || "").trim();
  return s ? s.charAt(0).toUpperCase() : "P";
}

const Pond = () => {
  const [ponds, setPonds] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [scopedAccountId, setScopedAccountId] = useState(() => getScopedAccountId());
  const navigate = useHistory();

  const userDetails = getUserDetails();
  const companyManager = isCompanyManagerUser(userDetails);
  const superSession = isSuperUserSession(userDetails);
  const staffId = getStaffId(userDetails);
  const fisherID = userDetails?.FisherID;
  const fisherWide = isFisherWideUser(userDetails);
  const independent = isIndependentScientistUser(userDetails);
  const skipFisherScope = independent || (fisherID == null || fisherID === "");
  const canCreatePond =
    (fisherWide && !companyManager && !superSession) ||
    ((companyManager || superSession) && scopedAccountId != null) ||
    (companyManager &&
      scopedAccountId == null &&
      userDetails?.sandboxFisherId != null &&
      userDetails.sandboxFisherId !== "");
  const createPondLabel =
    companyManager && scopedAccountId == null ? "Create Demo pond" : "Create pond";
  const createPondHref = "/createPond";
  const canDeletePond =
    hasScientistPondUIAccess(userDetails) &&
    !independent &&
    !companyManager &&
    !superSession;

  const fetchPondsNow = useCallback(async () => {
    const scopedId = getScopedAccountId();

    if (companyManager) {
      try {
        setIsLoading(true);
        const params = { page: currentPage, limit: entriesPerPage, search: searchTerm };
        if (scopedId) params.accountId = scopedId;
        const response = await axiosInstance.get("api/company/ponds", { params });
        const body = response.data;
        if (Array.isArray(body)) {
          setPonds(body);
          setTotalCount(body.length);
        } else {
          setPonds(body.data || []);
          setTotalCount(Number(body.total) || 0);
        }
      } catch (error) {
        showErrorToast(error.response?.data?.message || "Failed to fetch ponds");
      } finally {
        setIsLoading(false);
      }
      return;
    }

    if (superSession) {
      if (!scopedId) {
        setPonds([]);
        setTotalCount(0);
        return;
      }
      try {
        setIsLoading(true);
        const params = { page: currentPage, limit: entriesPerPage, search: searchTerm };
        const response = await axiosInstance.get(`api/Pond/GetAllPond/${scopedId}`, { params });
        const body = response.data;
        if (Array.isArray(body)) {
          setPonds(body);
          setTotalCount(body.length);
        } else {
          setPonds(body.data || []);
          setTotalCount(Number(body.total) || 0);
        }
      } catch (error) {
        showErrorToast(error.response?.data?.message || "Failed to fetch ponds");
      } finally {
        setIsLoading(false);
      }
      return;
    }

    if (!fisherWide && !skipFisherScope && (fisherID == null || fisherID === "")) return;
    if (!staffId) return;
    try {
      setIsLoading(true);
      const params = { page: currentPage, limit: entriesPerPage };
      let response;
      if (fisherWide) {
        params.search = searchTerm;
        response = await axiosInstance.get(`api/Pond/GetAllPond/${fisherID}`, { params });
      } else {
        if (!skipFisherScope && fisherID) {
          params.fisherID = fisherID;
        }
        response = await axiosInstance.get(`api/Pond/GetAllPondsByStaff/${staffId}`, { params });
      }
      const body = response.data;
      if (Array.isArray(body)) {
        setPonds(body);
        setTotalCount(body.length);
      } else {
        setPonds(body.data || []);
        setTotalCount(Number(body.total) || 0);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to fetch ponds";
      showErrorToast(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [
    companyManager,
    superSession,
    fisherID,
    fisherWide,
    skipFisherScope,
    staffId,
    currentPage,
    entriesPerPage,
    searchTerm,
  ]);

  const CheckExpiry = useCallback(async () => {
    await CheckUserExpiry();
  }, []);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const syncScoped = () => setScopedAccountId(getScopedAccountId());
    window.addEventListener("pondstat-scoped-account-changed", syncScoped);
    window.addEventListener("storage", syncScoped);
    syncScoped();
    return () => {
      window.removeEventListener("pondstat-scoped-account-changed", syncScoped);
      window.removeEventListener("storage", syncScoped);
    };
  }, []);

  useEffect(() => {
    checkTokenValidity();
    CheckExpiry();
  }, [CheckExpiry]);

  useEffect(() => {
    const delay = searchTerm ? 800 : 0;
    const id = setTimeout(() => fetchPondsNow(), delay);
    return () => clearTimeout(id);
  }, [searchTerm, currentPage, fetchPondsNow, scopedAccountId]);

  const DeletePond = async (pondId) => {
    const result = await Swal.fire({
      title: "Delete this pond?",
      text: "Associated data may be affected. This cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#14532d",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        await axiosInstance.delete(`api/Pond/DeletePond/${pondId}`);
        showSuccessToast("Pond deleted successfully");
        await fetchPondsNow();
      } catch (error) {
        const errorMessage = error.response?.data?.message || "Failed to delete pond";
        showErrorToast(errorMessage);
      }
    }
  };

  const indexOfFirstEntry = totalCount === 0 ? 0 : (currentPage - 1) * entriesPerPage + 1;
  const indexOfLastEntry = Math.min(currentPage * entriesPerPage, totalCount);
  const totalPages = Math.ceil(totalCount / entriesPerPage) || 1;

  const showAccountCol = !fisherWide || companyManager;
  const colCount = 6 + (showAccountCol ? 1 : 0) + 1;

  const emptyMessage = searchTerm
    ? "Try a different search, or clear the filter."
    : companyManager
      ? "No ponds on managed accounts yet."
      : fisherWide
        ? "Create your first pond to get started."
        : independent
          ? "Waiting for a pond owner invitation or pond assignment."
          : "You have no pond assignments yet.";

  const renderRowActions = (pond, alwaysVisible = false) => (
    <div className={`donezo-orders-actions${alwaysVisible ? " is-visible" : ""}`}>
      {companyManager ? (
        <>
          <button
            type="button"
            className="donezo-orders-actions__btn"
            title="Edit"
            aria-label="Edit pond"
            onClick={() => navigate.push(`/updatePond/${pond.id}`)}
          >
            <i className="fa fa-pen" aria-hidden />
          </button>
          <button
            type="button"
            className="donezo-orders-actions__btn"
            title="Visits"
            aria-label="Visits"
            onClick={() => navigate.push(`/pondDetailViaPond/${pond.id}`)}
          >
            <i className="fa fa-list" aria-hidden />
          </button>
          <button
            type="button"
            className="donezo-orders-actions__btn"
            title="Table view"
            aria-label="Table view"
            onClick={() => navigate.push("/FishDataTableView")}
          >
            <i className="fa fa-table" aria-hidden />
          </button>
          <button
            type="button"
            className="donezo-orders-actions__btn"
            title="Graph view"
            aria-label="Graph view"
            onClick={() => navigate.push("/fish-data-graph-new")}
          >
            <i className="fa fa-chart-line" aria-hidden />
          </button>
        </>
      ) : (
        <>
          <button
            type="button"
            className="donezo-orders-actions__btn"
            title="Edit pond"
            aria-label="Edit pond"
            onClick={() => navigate.push(`/updatePond/${pond.id}`)}
          >
            <i className="fa fa-pen" aria-hidden />
          </button>
          <button
            type="button"
            className="donezo-orders-actions__btn"
            title="Open pond"
            aria-label="Open pond"
            onClick={() => navigate.push(`/updatePond/${pond.id}`)}
          >
            <i className="fa fa-eye" aria-hidden />
          </button>
          {canDeletePond && (
            <button
              type="button"
              className="donezo-orders-actions__btn donezo-orders-actions__btn--danger"
              title="Delete pond"
              aria-label="Delete pond"
              onClick={() => DeletePond(pond.id)}
            >
              <i className="fa fa-trash" aria-hidden />
            </button>
          )}
        </>
      )}
    </div>
  );

  const renderEmptyState = (compact = false) => (
    <div className={`donezo-empty-state${compact ? " donezo-empty-state--compact" : ""}`}>
      {!compact && (
        <div className="donezo-empty-state__icon" aria-hidden>
          <i className="fa fa-water" />
        </div>
      )}
      <p className="donezo-empty-state__title">No ponds found</p>
      <p className="donezo-empty-state__text">
        {compact ? (searchTerm ? "Try another search." : "No ponds to show.") : emptyMessage}
      </p>
      {independent && !searchTerm && (
        <Link
          to="/scientistInvites"
          className="btn btn-outline-primary rounded-pill px-4 mt-3 d-inline-flex align-items-center"
        >
          View invitations
        </Link>
      )}
      {canCreatePond && !searchTerm && (
        <Link
          to={createPondHref}
          className="btn btn-primary rounded-pill px-4 mt-3 d-inline-flex align-items-center"
        >
          <i className="fa fa-plus me-2" aria-hidden />
          {createPondLabel}
        </Link>
      )}
    </div>
  );

  const pageNumbers = useMemo(() => {
    if (totalPages <= 5) return [...Array(totalPages)].map((_, i) => i + 1);
    const pages = new Set([1, totalPages, currentPage, currentPage - 1, currentPage + 1]);
    return [...pages].filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);
  }, [totalPages, currentPage]);

  return isLoading ? (
    <Preloader />
  ) : (
    <div className="donezo-page donezo-list-page donezo-orders-page">
      <div className="container-fluid px-3 px-md-4 py-3 py-md-4">
        <div className="donezo-orders-toolbar">
          <h1 className="donezo-orders-toolbar__title">Ponds</h1>

          <label htmlFor="pond-search" className="visually-hidden">
            Search ponds
          </label>
          <div className="donezo-orders-toolbar__search">
            <i className="fa fa-search" aria-hidden />
            <input
              id="pond-search"
              type="search"
              className="form-control"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              autoComplete="off"
            />
          </div>

          <div className="donezo-orders-toolbar__actions">
            {canCreatePond && (
              <Link to={createPondHref} className="donezo-orders-toolbar__primary">
                {windowWidth > 576 ? createPondLabel : "Add"}
              </Link>
            )}
          </div>
        </div>

        <div className="donezo-orders-panel">
          <div className="d-none d-md-block table-responsive">
            <table className="table align-middle mb-0 donezo-orders-table">
              <thead>
                <tr>
                  <th>Pond</th>
                  <th>Location</th>
                  <th>Type</th>
                  <th>Depth (ft)</th>
                  <th>Size (ac)</th>
                  {showAccountCol ? <th>Account</th> : null}
                  <th>Updated</th>
                  <th className="donezo-orders-table__actions-col" aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {ponds.length === 0 ? (
                  <tr>
                    <td colSpan={colCount} className="p-0 border-0">
                      {renderEmptyState()}
                    </td>
                  </tr>
                ) : (
                  ponds.map((pond) => (
                    <tr key={pond.id}>
                      <td>
                        <div className="donezo-orders-pond">
                          <span className="donezo-orders-pond__avatar" aria-hidden>
                            {pondInitial(pond.Name)}
                          </span>
                          <div className="donezo-orders-pond__meta">
                            <span className="donezo-orders-pond__name">{pond.Name}</span>
                            <span className="donezo-orders-pond__id">#{pond.id}</span>
                          </div>
                        </div>
                      </td>
                      <td>{pond.Location || "—"}</td>
                      <td>
                        {pond.Type ? (
                          <span className={`donezo-status-dot donezo-status-dot--${typeTone(pond.Type)}`}>
                            <span className="donezo-status-dot__mark" aria-hidden />
                            {pond.Type}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>{pond.Depth ?? "—"}</td>
                      <td>{pond.Size ?? "—"}</td>
                      {showAccountCol ? <td>{pond.FisherName || "—"}</td> : null}
                      <td className="donezo-orders-table__date">
                        {formatDisplayDate(pond.updatedAt)}
                      </td>
                      <td className="donezo-orders-table__actions-col">
                        {renderRowActions(pond)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="d-md-none donezo-orders-panel__mobile">
            {ponds.length === 0
              ? renderEmptyState(true)
              : ponds.map((pond) => (
                  <div key={pond.id} className="donezo-mobile-card">
                    <div className="donezo-mobile-card__head">
                      <div className="donezo-orders-pond">
                        <span className="donezo-orders-pond__avatar" aria-hidden>
                          {pondInitial(pond.Name)}
                        </span>
                        <div className="donezo-orders-pond__meta">
                          <h2 className="donezo-mobile-card__title mb-0">{pond.Name}</h2>
                          <span className="donezo-orders-pond__id">#{pond.id}</span>
                        </div>
                      </div>
                      {renderRowActions(pond, true)}
                    </div>
                    <div className="donezo-mobile-card__grid">
                      <div>
                        <span className="donezo-mobile-card__label">Location</span>
                        <div className="donezo-mobile-card__value">{pond.Location || "—"}</div>
                      </div>
                      <div>
                        <span className="donezo-mobile-card__label">Type</span>
                        <div className="donezo-mobile-card__value">
                          {pond.Type ? (
                            <span className={`donezo-status-dot donezo-status-dot--${typeTone(pond.Type)}`}>
                              <span className="donezo-status-dot__mark" aria-hidden />
                              {pond.Type}
                            </span>
                          ) : (
                            "—"
                          )}
                        </div>
                      </div>
                      <div>
                        <span className="donezo-mobile-card__label">Depth</span>
                        <div className="donezo-mobile-card__value">{pond.Depth ?? "—"}</div>
                      </div>
                      <div>
                        <span className="donezo-mobile-card__label">Size (ac)</span>
                        <div className="donezo-mobile-card__value">{pond.Size ?? "—"}</div>
                      </div>
                      {showAccountCol && (
                        <div className="donezo-mobile-card__full">
                          <span className="donezo-mobile-card__label">Account</span>
                          <div className="donezo-mobile-card__value">{pond.FisherName || "—"}</div>
                        </div>
                      )}
                      <div className="donezo-mobile-card__full">
                        <span className="donezo-mobile-card__label">Updated</span>
                        <div className="donezo-mobile-card__value">
                          {formatDisplayDate(pond.updatedAt)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
          </div>

          {ponds.length > 0 && (
            <div className="donezo-orders-footer">
              <div className="donezo-orders-footer__meta">
                Showing {indexOfFirstEntry}-{indexOfLastEntry} from {totalCount}
              </div>
              <ul className="donezo-orders-pagination">
                <li>
                  <button
                    type="button"
                    className="donezo-orders-pagination__btn"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    aria-label="Previous page"
                  >
                    <i className="fa fa-chevron-left" aria-hidden />
                  </button>
                </li>
                {windowWidth < 768 ? (
                  <li>
                    <span className="donezo-orders-pagination__btn is-active">
                      {currentPage}
                    </span>
                  </li>
                ) : (
                  pageNumbers.map((page, idx) => {
                    const prev = pageNumbers[idx - 1];
                    const showEllipsis = prev != null && page - prev > 1;
                    return (
                      <li key={page} className="d-inline-flex align-items-center gap-1">
                        {showEllipsis && (
                          <span className="donezo-orders-pagination__ellipsis">…</span>
                        )}
                        <button
                          type="button"
                          className={`donezo-orders-pagination__btn${
                            page === currentPage ? " is-active" : ""
                          }`}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </button>
                      </li>
                    );
                  })
                )}
                <li>
                  <button
                    type="button"
                    className="donezo-orders-pagination__btn"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    aria-label="Next page"
                  >
                    <i className="fa fa-chevron-right" aria-hidden />
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Pond;
